/**
 * More Service Content - IP, Compliance, Licenses, Legal Documents
 */

// ==================== INTELLECTUAL PROPERTY ====================

export const trademarkSearch = {
  id: "trademark-search",
  slug: "trademark-search",
  seoTitle: "Trademark Search India | Check Trademark Availability",
  seoDescription: "Comprehensive trademark search to check availability before filing. Avoid conflicts and rejection with professional TM search services.",
  heroTitle: "Trademark Search",
  heroSubtitle: "Intellectual Property",
  summary: "Trademark Search is a crucial first step before filing for trademark registration. It helps identify existing identical or similar trademarks that may conflict with your proposed mark. Our comprehensive search covers registered trademarks, pending applications, and common law marks to minimize rejection risk.",
  
  whoNeeds: {
    title: "Who Needs Trademark Search?",
    description: "Anyone planning to register a trademark should conduct a thorough search first.",
    points: [
      "Businesses planning to register a new trademark",
      "Companies launching new products or services",
      "Startups selecting brand names",
      "Businesses expanding to new markets",
      "Companies acquiring or licensing brands",
      "Businesses rebranding or updating logos",
      "Entrepreneurs before business name finalization",
      "Investors conducting IP due diligence"
    ]
  },
  
  whyRequired: {
    title: "Why is Trademark Search Important?",
    description: "A thorough search prevents costly conflicts and application rejections.",
    points: [
      "Avoid Rejection: Identify conflicting marks before spending on application fees.",
      "Prevent Litigation: Reduce risk of infringement claims from existing trademark owners.",
      "Save Time: Avoid lengthy opposition proceedings by selecting unique marks.",
      "Save Money: Avoid wasted filing fees and potential rebranding costs.",
      "Informed Decision: Make data-driven decisions about your brand strategy.",
      "Competitive Intelligence: Understand trademark landscape in your industry."
    ]
  },
  
  benefits: [
    { title: "Risk Reduction", description: "Identify potential conflicts before investing in registration and branding." },
    { title: "Cost Savings", description: "Avoid wasted application fees from rejected trademarks." },
    { title: "Strategic Planning", description: "Make informed decisions about brand naming strategy." },
    { title: "Comprehensive Coverage", description: "Search across registered, pending, and common law marks." },
    { title: "Expert Analysis", description: "Professional assessment of conflict risks and registrability." },
    { title: "Quick Results", description: "Receive detailed search report within 24-48 hours." },
    { title: "Confidence", description: "Proceed with registration knowing your mark is likely unique." },
    { title: "Multiple Classes", description: "Search across relevant trademark classes for complete coverage." }
  ],
  
  documentsRequired: {
    title: "Information for Trademark Search",
    description: "Minimal information needed to conduct comprehensive search.",
    directors: [
      "Contact details for communication",
      "Business name (optional)"
    ],
    company: [
      "Proposed trademark (word/logo/both)",
      "Goods/services description",
      "Target trademark classes",
      "Logo image (if applicable)",
      "Phonetic variations to search",
      "Similar marks to compare"
    ]
  },
  
  process: [
    { step: 1, title: "Information Gathering", description: "Collect details about proposed mark, goods/services, and target classes.", duration: "Same day" },
    { step: 2, title: "Database Search", description: "Search IP India database for identical and similar registered marks.", duration: "1 day" },
    { step: 3, title: "Pending Applications", description: "Check pending applications that may conflict.", duration: "Included" },
    { step: 4, title: "Phonetic Analysis", description: "Search phonetically similar marks that sound alike.", duration: "Included" },
    { step: 5, title: "Report Preparation", description: "Compile comprehensive report with findings and analysis.", duration: "1 day" },
    { step: 6, title: "Expert Consultation", description: "Review findings with trademark expert for recommendations.", duration: "Included" }
  ],
  
  timeline: {
    total: "24-48 hours",
    breakdown: [
      { phase: "Information collection", duration: "Same day" },
      { phase: "Database search", duration: "1 day" },
      { phase: "Report generation", duration: "1 day" }
    ],
    note: "Express search available in 4 hours for urgent requirements."
  },
  
  pricing: {
    title: "Trademark Search Pricing",
    description: "Comprehensive search packages for confident trademark decisions.",
    packages: [
      {
        name: "Basic Search",
        price: "₹999",
        description: "Single class search",
        features: [
          "Identical mark search",
          "Single class coverage",
          "PDF report",
          "Email delivery",
          "Basic recommendations"
        ]
      },
      {
        name: "Comprehensive",
        price: "₹2,499",
        description: "Multi-class with analysis",
        features: [
          "Identical + similar marks",
          "Up to 3 classes",
          "Phonetic search",
          "Detailed analysis",
          "Expert consultation"
        ],
        popular: true
      },
      {
        name: "Premium",
        price: "₹4,999",
        description: "Full clearance search",
        features: [
          "All classes coverage",
          "Common law search",
          "Domain name check",
          "Written legal opinion",
          "Registration strategy"
        ]
      }
    ],
    governmentFees: "No government fees for trademark search."
  },
  
  faqs: [
    { question: "Is trademark search mandatory before filing?", answer: "Not legally mandatory, but highly recommended. A search can save significant costs by avoiding conflicts and likely rejections." },
    { question: "What databases are searched?", answer: "We search the IP India trademark database covering all registered marks and pending applications, along with phonetic and visual similarity analysis." },
    { question: "Can search guarantee registration approval?", answer: "No search can guarantee approval as examiners may identify conflicts not apparent in searches, and new applications may be filed. However, search significantly reduces rejection risk." },
    { question: "How detailed is the search report?", answer: "Our report includes all conflicting marks found, similarity analysis, risk assessment, and recommendations on whether to proceed with registration." },
    { question: "Should I search before finalizing my brand name?", answer: "Yes, we recommend conducting a trademark search before finalizing any brand name or logo to avoid costly rebranding later." },
    { question: "What if conflicts are found?", answer: "If conflicts are found, we provide recommendations on modifying the mark, choosing alternative names, or strategies to distinguish your mark." }
  ],
  
  relatedServices: [
    { id: "trademark-registration", label: "Trademark Registration" },
    { id: "trademark-objection", label: "Trademark Objection" },
    { id: "copyright-registration", label: "Copyright Registration" },
    { id: "private-limited-company", label: "Company Registration" },
    { id: "logo-design", label: "Logo Design" },
    { id: "brand-strategy", label: "Brand Strategy" }
  ],
  
  trustSignals: {
    incorporations: "15,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "IP Attorneys"
  }
};

export const trademarkObjection = {
  id: "trademark-objection",
  slug: "trademark-objection",
  seoTitle: "Trademark Objection Reply | Respond to TM Examiner",
  seoDescription: "Expert response to trademark examination report objections. Increase approval chances with professional objection handling by IP attorneys.",
  heroTitle: "Trademark Objection Response",
  heroSubtitle: "Intellectual Property",
  summary: "When your trademark application receives an examination report with objections, you must respond within 30 days. Our IP experts analyze objection grounds, prepare compelling responses with legal arguments and evidence to overcome rejections and secure your trademark registration.",
  
  whoNeeds: {
    title: "Who Needs Objection Response Services?",
    description: "Anyone who has received an examination report with objections on their trademark application.",
    points: [
      "Applicants with 'Objected' status on TM application",
      "Applications facing descriptiveness objections",
      "Marks objected for similarity to existing trademarks",
      "Applications lacking distinctiveness",
      "Marks objected under Section 9 or 11",
      "Applications with multiple grounds of objection",
      "Self-filed applications facing technical objections",
      "Expired objection deadline needing restoration"
    ]
  },
  
  whyRequired: {
    title: "Why is Professional Response Important?",
    description: "Proper objection response can make the difference between registration and rejection.",
    points: [
      "Time Sensitive: Only 30 days to respond; missed deadline means abandonment.",
      "Legal Arguments: Technical legal reasoning needed to overcome objections.",
      "Evidence Requirements: Proper documentation and affidavits required.",
      "Success Rate: Professional responses have significantly higher success rates.",
      "Hearing Preparation: May require appearance at trademark hearing.",
      "Appeals: Failed responses can still be appealed with proper strategy."
    ]
  },
  
  benefits: [
    { title: "Expert Analysis", description: "IP attorneys analyze objection grounds and identify best response strategy." },
    { title: "Legal Arguments", description: "Craft compelling legal arguments citing relevant case law and precedents." },
    { title: "Evidence Preparation", description: "Compile supporting evidence, affidavits, and documentation." },
    { title: "Higher Success", description: "Professional responses have significantly higher approval rates." },
    { title: "Hearing Support", description: "Representation at trademark hearing if required." },
    { title: "Deadline Management", description: "Ensure timely filing within statutory deadlines." },
    { title: "Multiple Objections", description: "Address all objection grounds comprehensively." },
    { title: "Follow-up", description: "Track application status and handle further queries." }
  ],
  
  documentsRequired: {
    title: "Documents for Objection Response",
    description: "Documentation needed depends on objection grounds.",
    directors: [
      "Applicant identity proof",
      "Authorization letter (if represented)",
      "Affidavit of use (if claiming prior use)"
    ],
    company: [
      "Copy of examination report",
      "Original trademark application",
      "Proof of prior use (invoices, advertisements)",
      "Evidence of acquired distinctiveness",
      "Sales figures and market presence",
      "Consent letters (if similarity objection)",
      "Third-party registrations for similar marks"
    ]
  },
  
  process: [
    { step: 1, title: "Objection Analysis", description: "Detailed analysis of examination report and objection grounds.", duration: "1 day" },
    { step: 2, title: "Strategy Development", description: "Identify best legal arguments and evidence to overcome objections.", duration: "1 day" },
    { step: 3, title: "Evidence Collection", description: "Gather supporting documents, affidavits, and proof of use.", duration: "2-3 days" },
    { step: 4, title: "Response Drafting", description: "Prepare comprehensive reply citing legal provisions and precedents.", duration: "2 days" },
    { step: 5, title: "Filing", description: "Submit response through IP India portal before deadline.", duration: "Same day" },
    { step: 6, title: "Follow-up", description: "Track status, attend hearing if required, handle queries.", duration: "Ongoing" }
  ],
  
  timeline: {
    total: "5-7 business days",
    breakdown: [
      { phase: "Analysis & strategy", duration: "1-2 days" },
      { phase: "Evidence & drafting", duration: "3-4 days" },
      { phase: "Filing", duration: "1 day" }
    ],
    note: "Response must be filed within 30 days of examination report. Extension available on request."
  },
  
  pricing: {
    title: "Objection Response Fees",
    description: "Professional fees based on objection complexity.",
    packages: [
      {
        name: "Basic Objection",
        price: "₹4,999",
        description: "Single ground objection",
        features: [
          "Objection analysis",
          "Response drafting",
          "Online filing",
          "Status tracking",
          "Basic follow-up"
        ]
      },
      {
        name: "Complex Objection",
        price: "₹8,999",
        description: "Multiple grounds/similarity",
        features: [
          "Detailed legal analysis",
          "Evidence compilation",
          "Comprehensive response",
          "Hearing representation",
          "Full support till decision"
        ],
        popular: true
      },
      {
        name: "Premium",
        price: "₹14,999",
        description: "Difficult cases + appeal",
        features: [
          "Senior IP attorney",
          "Extensive research",
          "Multiple responses",
          "Appeal preparation",
          "Complete representation"
        ]
      }
    ],
    governmentFees: "Government filing fees approximately ₹2,500-5,000 for response submission."
  },
  
  faqs: [
    { question: "What happens if I don't respond to objection?", answer: "If no response is filed within 30 days (extendable to 60 days on request), the application is treated as abandoned and removed from the register." },
    { question: "Can all objections be overcome?", answer: "Many objections can be overcome with proper legal arguments. Success depends on objection grounds, evidence available, and response quality. Some absolute grounds may be difficult to overcome." },
    { question: "What is a trademark hearing?", answer: "If examiner is not satisfied with written response, a hearing is scheduled where you can present oral arguments. Professional representation is recommended." },
    { question: "What if response is rejected?", answer: "You can file an appeal with the Intellectual Property Appellate Board (IPAB) within 3 months of rejection order." },
    { question: "What are common objection grounds?", answer: "Common grounds include: descriptiveness, lack of distinctiveness, similarity to existing marks, deceptive marks, and geographical indications." },
    { question: "Can deadline be extended?", answer: "Yes, 30-day extension is available on request. However, filing within original deadline is recommended for faster processing." }
  ],
  
  relatedServices: [
    { id: "trademark-registration", label: "Trademark Registration" },
    { id: "trademark-search", label: "Trademark Search" },
    { id: "trademark-opposition", label: "Trademark Opposition" },
    { id: "trademark-renewal", label: "Trademark Renewal" },
    { id: "copyright-registration", label: "Copyright Registration" },
    { id: "brand-protection", label: "Brand Protection" }
  ],
  
  trustSignals: {
    incorporations: "5,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "IP Attorneys"
  }
};

export const copyrightRegistration = {
  id: "copyright-registration",
  slug: "copyright-registration",
  seoTitle: "Copyright Registration India | Protect Creative Works",
  seoDescription: "Register copyright for books, music, software, artwork. Protect your creative works with official registration. Expert copyright filing services.",
  heroTitle: "Copyright Registration",
  heroSubtitle: "Intellectual Property",
  summary: "Copyright Registration provides legal protection for original creative and literary works including books, music, films, software, artwork, and photographs. While copyright exists automatically upon creation, registration provides prima facie evidence of ownership, enables legal action against infringement, and establishes public record of your rights.",
  
  whoNeeds: {
    title: "Who Should Register Copyright?",
    description: "Creators of original works should register to protect and enforce their rights.",
    points: [
      "Authors and writers for books, articles, scripts",
      "Musicians and composers for songs and compositions",
      "Software developers for programs and code",
      "Artists for paintings, sculptures, photographs",
      "Filmmakers for movies and documentaries",
      "Businesses for marketing content and branding",
      "Architects for architectural designs",
      "Website owners for content and design"
    ]
  },
  
  whyRequired: {
    title: "Why Register Your Copyright?",
    description: "Registration provides significant legal and commercial advantages.",
    points: [
      "Legal Evidence: Registration is prima facie evidence of ownership in court.",
      "Infringement Action: Required for filing infringement lawsuits in some cases.",
      "Statutory Damages: May be eligible for statutory damages and attorney fees.",
      "Public Record: Creates official public record of your ownership claim.",
      "Deterrent Effect: Registration deters potential infringers.",
      "Licensing: Facilitates licensing and monetization of creative works."
    ]
  },
  
  benefits: [
    { title: "Proof of Ownership", description: "Official certificate serves as prima facie evidence of copyright ownership." },
    { title: "Legal Protection", description: "Enables legal action against unauthorized copying and infringement." },
    { title: "Public Record", description: "Creates searchable public record of your creative works." },
    { title: "Monetization", description: "Facilitates licensing, selling, or assigning copyright." },
    { title: "Deterrence", description: "Registered copyright deters potential infringers." },
    { title: "International", description: "Indian registration recognized in Berne Convention countries." },
    { title: "Long Duration", description: "Copyright lasts for author's lifetime plus 60 years." },
    { title: "Asset Value", description: "Creates intangible asset for business valuation." }
  ],
  
  documentsRequired: {
    title: "Documents for Copyright Registration",
    description: "Documentation requirements vary based on type of work.",
    directors: [
      "Identity proof of author/applicant",
      "Address proof",
      "Passport-size photograph",
      "NOC from author (if applicant is different)"
    ],
    company: [
      "Copies of the work (3 copies)",
      "Statement of particulars (Form XIV)",
      "Power of attorney",
      "Certificate of publication (if published)",
      "Assignment deed (if rights assigned)",
      "Declaration of authorship"
    ]
  },
  
  process: [
    { step: 1, title: "Work Classification", description: "Determine category of work and applicable registration requirements.", duration: "Same day" },
    { step: 2, title: "Application Preparation", description: "Prepare Form XIV and supporting documents.", duration: "1-2 days" },
    { step: 3, title: "Filing", description: "Submit application to Copyright Office with prescribed fees.", duration: "1 day" },
    { step: 4, title: "Diary Number", description: "Receive diary number and wait for mandatory waiting period.", duration: "Immediate" },
    { step: 5, title: "Examination", description: "Copyright Office examines application after 30-day waiting period.", duration: "30-60 days" },
    { step: 6, title: "Registration", description: "Receive Copyright Registration Certificate upon approval.", duration: "On approval" }
  ],
  
  timeline: {
    total: "2-4 months",
    breakdown: [
      { phase: "Application preparation", duration: "2-3 days" },
      { phase: "Mandatory waiting period", duration: "30 days" },
      { phase: "Examination", duration: "30-60 days" },
      { phase: "Registration", duration: "7-15 days" }
    ],
    note: "Mandatory 30-day waiting period allows for objections. Timeline may extend if objections are raised."
  },
  
  pricing: {
    title: "Copyright Registration Fees",
    description: "Affordable registration for all types of creative works.",
    packages: [
      {
        name: "Literary Work",
        price: "₹5,999",
        description: "Books, articles, scripts",
        features: [
          "Application drafting",
          "Form XIV preparation",
          "Online filing",
          "Objection handling",
          "Certificate delivery"
        ]
      },
      {
        name: "Artistic/Music",
        price: "₹7,999",
        description: "Art, music, photographs",
        features: [
          "Everything in Literary",
          "Multiple format support",
          "Detailed classification",
          "Rights documentation",
          "Licensing guidance"
        ],
        popular: true
      },
      {
        name: "Software",
        price: "₹11,999",
        description: "Software and digital works",
        features: [
          "Source code handling",
          "Confidentiality protocols",
          "Technical documentation",
          "Trade secret advice",
          "Comprehensive protection"
        ]
      }
    ],
    governmentFees: "Government fees: ₹500 for literary, ₹500 for artistic, ₹2,000 for software (per work)."
  },
  
  faqs: [
    { question: "Is copyright registration mandatory?", answer: "No, copyright exists automatically upon creation. However, registration provides legal evidence, enables enforcement, and offers additional benefits." },
    { question: "How long does copyright last?", answer: "For literary, dramatic, musical works: Author's lifetime plus 60 years. For photographs, films, sound recordings: 60 years from publication." },
    { question: "Can I register unpublished works?", answer: "Yes, both published and unpublished works can be registered. In fact, early registration of unpublished works provides earlier protection." },
    { question: "What is the 30-day waiting period?", answer: "Mandatory waiting period after filing during which anyone can raise objections. If no objections, application proceeds to examination." },
    { question: "Can I register software code?", answer: "Yes, software source code and object code can be registered as literary works. Special provisions exist for software copyright." },
    { question: "Is Indian copyright valid internationally?", answer: "India is signatory to Berne Convention. Indian copyright is recognized in all member countries without need for separate registration." }
  ],
  
  relatedServices: [
    { id: "trademark-registration", label: "Trademark Registration" },
    { id: "patent-registration", label: "Patent Registration" },
    { id: "software-licensing", label: "Software Licensing" },
    { id: "nda-agreement", label: "NDA Drafting" },
    { id: "ip-strategy", label: "IP Strategy" },
    { id: "infringement", label: "Infringement Action" }
  ],
  
  trustSignals: {
    incorporations: "8,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "IP Attorneys"
  }
};

// ==================== LICENSES ====================

export const fssaiLicense = {
  id: "fssai-license",
  slug: "fssai-license",
  seoTitle: "FSSAI License Registration | Food License Online",
  seoDescription: "Get FSSAI food license for your food business. Basic, State, and Central license registration. Expert assistance for restaurants, manufacturers, and food traders.",
  heroTitle: "FSSAI License Registration",
  heroSubtitle: "Business License",
  summary: "FSSAI (Food Safety and Standards Authority of India) License is mandatory for all food businesses in India. Whether you run a restaurant, manufacture food products, or trade in food items, you need appropriate FSSAI registration based on your business size and turnover. We handle all types of FSSAI licenses with quick processing.",
  
  whoNeeds: {
    title: "Who Needs FSSAI License?",
    description: "Any business involved in food manufacturing, processing, storage, or distribution.",
    points: [
      "Restaurants, cafes, and food service establishments",
      "Food manufacturers and processors",
      "Food retailers and traders",
      "Hotels and catering businesses",
      "Food importers and exporters",
      "Food storage and cold chain operators",
      "Online food delivery and cloud kitchens",
      "Food packaging and labeling businesses"
    ]
  },
  
  whyRequired: {
    title: "Why is FSSAI License Mandatory?",
    description: "FSSAI license ensures food safety and is legally required for food businesses.",
    points: [
      "Legal Requirement: Mandatory under Food Safety and Standards Act, 2006.",
      "Consumer Safety: Ensures food products meet safety and quality standards.",
      "Business Credibility: Builds trust with customers and business partners.",
      "Market Access: Required for selling through modern retail and e-commerce.",
      "Avoid Penalties: Operating without license attracts fines up to ₹5 lakh.",
      "Export Requirement: Essential for exporting food products."
    ]
  },
  
  benefits: [
    { title: "Legal Compliance", description: "Operate your food business legally without fear of penalties." },
    { title: "Consumer Trust", description: "FSSAI logo on products builds customer confidence." },
    { title: "Market Access", description: "Sell through supermarkets, e-commerce platforms, and exports." },
    { title: "Quality Assurance", description: "Demonstrates commitment to food safety standards." },
    { title: "Business Growth", description: "Required for business expansion and partnerships." },
    { title: "Bank Loans", description: "Facilitates business loans for food industry." },
    { title: "Brand Value", description: "Enhances brand credibility in competitive market." },
    { title: "Tender Eligibility", description: "Required for government and institutional contracts." }
  ],
  
  documentsRequired: {
    title: "Documents for FSSAI License",
    description: "Documentation varies based on license type and business category.",
    directors: [
      "Identity proof (Aadhaar/PAN/Voter ID)",
      "Passport-size photograph",
      "Form IX declaration",
      "Nomination for license"
    ],
    company: [
      "Business registration documents",
      "Proof of premises (rent/ownership)",
      "Partnership deed/MOA (if applicable)",
      "Food category details",
      "List of food products",
      "Production capacity details",
      "Water test report (for manufacturing)",
      "NOC from municipal authority"
    ]
  },
  
  process: [
    { step: 1, title: "License Type Selection", description: "Determine appropriate license type based on turnover and business nature.", duration: "Same day" },
    { step: 2, title: "Document Preparation", description: "Compile required documents based on license category.", duration: "1-2 days" },
    { step: 3, title: "Application Filing", description: "Submit application on FSSAI FoSCoS portal with fees.", duration: "1 day" },
    { step: 4, title: "Inspection", description: "FSSAI inspector may conduct premises inspection.", duration: "Variable" },
    { step: 5, title: "Query Response", description: "Address any queries or additional requirements from FSSAI.", duration: "As needed" },
    { step: 6, title: "License Issuance", description: "Receive FSSAI license certificate with unique number.", duration: "7-30 days" }
  ],
  
  timeline: {
    total: "7-30 days",
    breakdown: [
      { phase: "Basic Registration", duration: "7-15 days" },
      { phase: "State License", duration: "15-30 days" },
      { phase: "Central License", duration: "30-60 days" }
    ],
    note: "Timeline varies based on license type and inspection requirements. Basic registration is fastest."
  },
  
  pricing: {
    title: "FSSAI License Pricing",
    description: "Complete registration service including government fees for 1 year.",
    packages: [
      {
        name: "Basic Registration",
        price: "₹2,999",
        description: "Turnover up to ₹12 lakh",
        features: [
          "Basic registration filing",
          "Document preparation",
          "Portal submission",
          "1 year license",
          "Certificate delivery"
        ]
      },
      {
        name: "State License",
        price: "₹5,999",
        description: "Turnover ₹12 lakh - ₹20 crore",
        features: [
          "State license application",
          "All documentation",
          "Inspection coordination",
          "Query handling",
          "1-5 year options"
        ],
        popular: true
      },
      {
        name: "Central License",
        price: "₹9,999",
        description: "Turnover above ₹20 crore",
        features: [
          "Central license filing",
          "Complex applications",
          "Multi-state coverage",
          "Complete compliance",
          "Dedicated support"
        ]
      }
    ],
    governmentFees: "Government fees: Basic ₹100/year, State ₹2,000-5,000/year, Central ₹7,500/year."
  },
  
  faqs: [
    { question: "Which FSSAI license do I need?", answer: "Basic: Turnover up to ₹12 lakh. State: ₹12 lakh to ₹20 crore. Central: Above ₹20 crore or interstate operations. Specific categories like importers always need Central." },
    { question: "What is the validity of FSSAI license?", answer: "FSSAI licenses can be obtained for 1 to 5 years. Renewal must be filed 30 days before expiry." },
    { question: "Can I operate without FSSAI license?", answer: "No, operating without license is illegal. Penalties include fines up to ₹5 lakh and imprisonment up to 6 months for serious violations." },
    { question: "Is inspection mandatory?", answer: "Inspection depends on license type and risk category. Manufacturing units typically require inspection while basic registration may not." },
    { question: "What is FSSAI 14-digit number?", answer: "Upon registration, you receive a unique 14-digit license number to be displayed on food products and premises." },
    { question: "Can I upgrade license later?", answer: "Yes, you can upgrade from Basic to State or State to Central as your business grows. Fresh application required for upgrade." }
  ],
  
  relatedServices: [
    { id: "shop-establishment", label: "Shop License" },
    { id: "trade-license", label: "Trade License" },
    { id: "gst-registration", label: "GST Registration" },
    { id: "trademark-registration", label: "Trademark" },
    { id: "iso-certification", label: "ISO Certification" },
    { id: "import-export-code", label: "IEC Code" }
  ],
  
  trustSignals: {
    incorporations: "10,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Expert Support"
  }
};

export const iecCode = {
  id: "iec-code",
  slug: "iec-code",
  seoTitle: "IEC Code Registration | Import Export License India",
  seoDescription: "Get Import Export Code (IEC) for international trade. Mandatory for importing and exporting goods and services. Quick online registration with expert assistance.",
  heroTitle: "IEC Code Registration",
  heroSubtitle: "Business License",
  summary: "Import Export Code (IEC) is a mandatory 10-digit code issued by DGFT (Directorate General of Foreign Trade) for businesses engaged in import or export of goods and services. It's a one-time registration valid for lifetime without renewal. Our experts ensure quick IEC registration with proper documentation.",
  
  whoNeeds: {
    title: "Who Needs IEC Code?",
    description: "Any individual or business planning to import or export goods or services.",
    points: [
      "Businesses importing goods from other countries",
      "Exporters of goods and products",
      "Service providers exporting services (IT, consulting)",
      "E-commerce sellers sourcing internationally",
      "Manufacturers importing raw materials",
      "Trading companies in international trade",
      "Freelancers receiving foreign payments",
      "Businesses receiving foreign investments"
    ]
  },
  
  whyRequired: {
    title: "Why is IEC Mandatory?",
    description: "IEC is the primary document for conducting import-export business in India.",
    points: [
      "Legal Requirement: No import/export possible without valid IEC.",
      "Customs Clearance: Required for clearing goods at customs.",
      "Bank Transactions: Necessary for foreign currency transactions.",
      "Incentives: Eligibility for export promotion schemes and subsidies.",
      "Service Exports: Required for receiving payments for exported services.",
      "Lifetime Validity: One-time registration valid forever."
    ]
  },
  
  benefits: [
    { title: "International Trade", description: "Legal authorization to import and export goods and services." },
    { title: "Customs Clearance", description: "Required for clearing imported goods at ports." },
    { title: "Bank Remittances", description: "Enables receiving foreign currency payments." },
    { title: "Export Benefits", description: "Access to MEIS, SEIS, and other export incentives." },
    { title: "Lifetime Valid", description: "No renewal required - one-time registration." },
    { title: "GST Integration", description: "Links with GST for seamless export operations." },
    { title: "E-commerce", description: "Required for international e-commerce selling." },
    { title: "Professional Image", description: "Enhances credibility in international markets." }
  ],
  
  documentsRequired: {
    title: "Documents for IEC Registration",
    description: "Minimal documentation required for quick processing.",
    directors: [
      "PAN card of applicant/authorized signatory",
      "Aadhaar card/Voter ID",
      "Passport-size photograph",
      "Digital Signature Certificate (optional)"
    ],
    company: [
      "Entity PAN card",
      "Business registration certificate",
      "Bank account details with cancelled cheque",
      "Bank certificate format (ANF-2A)",
      "Registered office address proof",
      "Partnership deed/MOA (if applicable)"
    ]
  },
  
  process: [
    { step: 1, title: "PAN Verification", description: "Verify PAN details and entity registration documents.", duration: "Same day" },
    { step: 2, title: "Bank Certificate", description: "Obtain bank certificate in prescribed format from your bank.", duration: "1-2 days" },
    { step: 3, title: "Application Filing", description: "Submit ANF-2A application on DGFT portal.", duration: "1 day" },
    { step: 4, title: "Fee Payment", description: "Pay application fee online through DGFT portal.", duration: "Instant" },
    { step: 5, title: "DGFT Processing", description: "DGFT verifies documents and processes application.", duration: "1-3 days" },
    { step: 6, title: "IEC Allotment", description: "Receive IEC certificate via email.", duration: "On approval" }
  ],
  
  timeline: {
    total: "3-5 business days",
    breakdown: [
      { phase: "Document preparation", duration: "1-2 days" },
      { phase: "Application filing", duration: "1 day" },
      { phase: "DGFT processing", duration: "1-3 days" }
    ],
    note: "Process is largely online and automated. Delays occur only if documents need clarification."
  },
  
  pricing: {
    title: "IEC Registration Pricing",
    description: "Complete IEC registration with lifetime validity.",
    packages: [
      {
        name: "Basic IEC",
        price: "₹1,999",
        description: "Standard IEC registration",
        features: [
          "Application preparation",
          "DGFT portal filing",
          "Fee payment assistance",
          "Certificate delivery",
          "Basic support"
        ]
      },
      {
        name: "Express IEC",
        price: "₹3,999",
        description: "Priority processing",
        features: [
          "Same day filing",
          "Priority support",
          "Bank certificate assistance",
          "DSC assistance",
          "Faster processing"
        ],
        popular: true
      },
      {
        name: "Business Package",
        price: "₹6,999",
        description: "IEC + AD Code + GST LUT",
        features: [
          "IEC registration",
          "AD Code registration",
          "GST LUT filing",
          "Export documentation",
          "Dedicated support"
        ]
      }
    ],
    governmentFees: "Government fee: ₹500 (online payment to DGFT)."
  },
  
  faqs: [
    { question: "Who is exempt from IEC?", answer: "Personal imports/exports for non-commercial purposes, and certain government departments/charitable organizations are exempt. Most businesses need IEC." },
    { question: "Is IEC linked to PAN?", answer: "Yes, your IEC number is typically your PAN number. Since 2017, PAN is used as IEC for most cases." },
    { question: "What is the validity of IEC?", answer: "IEC is valid for lifetime. No renewal required. However, annual update of details on DGFT portal is recommended." },
    { question: "Can individuals get IEC?", answer: "Yes, individuals can obtain IEC for import-export business. Proprietors use their personal PAN as IEC." },
    { question: "Do I need separate IEC for each branch?", answer: "No, one IEC covers all branches. However, each branch address can be added to the IEC record." },
    { question: "What is AD Code?", answer: "AD (Authorized Dealer) Code is the code of your bank branch through which you'll route foreign transactions. Required separately for customs." }
  ],
  
  relatedServices: [
    { id: "gst-lut-filing", label: "GST LUT Filing" },
    { id: "gst-registration", label: "GST Registration" },
    { id: "fssai-license", label: "FSSAI License" },
    { id: "trademark-registration", label: "Trademark" },
    { id: "private-limited-company", label: "Company Registration" },
    { id: "ad-code-registration", label: "AD Code" }
  ],
  
  trustSignals: {
    incorporations: "12,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Export Experts"
  }
};

// ==================== COMPLIANCE ====================

export const annualFiling = {
  id: "annual-filing",
  slug: "annual-filing",
  seoTitle: "Annual Compliance Filing | ROC Filing Services",
  seoDescription: "Complete annual compliance for companies and LLPs. MCA annual return, financial statements, AGM, and ROC filing services. Expert CA assistance.",
  heroTitle: "Annual Compliance Filing",
  heroSubtitle: "Corporate Compliance",
  summary: "Annual Compliance Filing includes mandatory filings with Registrar of Companies (ROC) for all registered companies and LLPs. This includes Annual Return, Financial Statements, Director KYC, and other statutory requirements. Non-compliance attracts heavy penalties and can lead to director disqualification.",
  
  whoNeeds: {
    title: "Who Needs Annual Filing Services?",
    description: "All registered companies and LLPs must complete annual compliance.",
    points: [
      "Private Limited Companies",
      "Public Limited Companies",
      "One Person Companies (OPC)",
      "Limited Liability Partnerships (LLPs)",
      "Section 8 Companies",
      "Producer Companies",
      "Dormant companies",
      "Companies with nil transactions"
    ]
  },
  
  whyRequired: {
    title: "Why is Annual Filing Critical?",
    description: "Annual compliance is mandatory under Companies Act with severe penalties for default.",
    points: [
      "Legal Mandate: Statutory requirement under Companies Act, 2013 and LLP Act, 2008.",
      "Avoid Penalties: Late filing attracts additional fees up to 12x of normal fees.",
      "Director Protection: Non-filing can lead to director disqualification and prosecution.",
      "Company Status: Consistent non-filing leads to company being struck off.",
      "Bank & Contracts: Banks and businesses verify MCA filings before transactions.",
      "Due Diligence: Investors and acquirers check compliance history."
    ]
  },
  
  benefits: [
    { title: "Legal Compliance", description: "Stay compliant with Companies Act and avoid penalties." },
    { title: "Director Protection", description: "Protect directors from disqualification and prosecution." },
    { title: "Active Status", description: "Maintain 'Active' status on MCA records." },
    { title: "Credibility", description: "Clean compliance record enhances business credibility." },
    { title: "Bank Relations", description: "Banks verify ROC filings for loans and facilities." },
    { title: "Investment Ready", description: "Clean records facilitate fundraising and due diligence." },
    { title: "Expert Review", description: "CA-reviewed filings ensure accuracy." },
    { title: "Deadline Management", description: "Never miss a filing deadline with our reminders." }
  ],
  
  documentsRequired: {
    title: "Documents for Annual Filing",
    description: "Documents required for complete annual compliance.",
    directors: [
      "Director DIN details",
      "Digital Signature Certificates",
      "Director KYC documents",
      "Residential address proof"
    ],
    company: [
      "Audited financial statements",
      "Board resolutions",
      "AGM minutes",
      "Shareholder details",
      "Previous year filings",
      "Bank statements",
      "Tax returns",
      "Statutory registers"
    ]
  },
  
  process: [
    { step: 1, title: "Document Collection", description: "Gather financial statements, board resolutions, and shareholder details.", duration: "1-3 days" },
    { step: 2, title: "Financial Review", description: "Review and finalize audited financial statements.", duration: "2-5 days" },
    { step: 3, title: "AGM Compliance", description: "Prepare AGM notice, conduct meeting, record minutes.", duration: "As scheduled" },
    { step: 4, title: "Form Preparation", description: "Prepare AOC-4/MGT-7/Form 8/Form 11 as applicable.", duration: "2-3 days" },
    { step: 5, title: "Director KYC", description: "Complete DIR-3 KYC for all directors.", duration: "1 day" },
    { step: 6, title: "ROC Filing", description: "File all forms with MCA portal and obtain SRN.", duration: "1 day" }
  ],
  
  timeline: {
    total: "7-15 business days",
    breakdown: [
      { phase: "Document collection", duration: "3-5 days" },
      { phase: "Financial review", duration: "2-5 days" },
      { phase: "Form preparation & filing", duration: "2-5 days" }
    ],
    note: "AOC-4 due within 30 days of AGM. MGT-7 due within 60 days of AGM. AGM within 6 months of FY end."
  },
  
  pricing: {
    title: "Annual Compliance Pricing",
    description: "Comprehensive annual filing packages.",
    packages: [
      {
        name: "Startup",
        price: "₹7,999",
        description: "For small companies (turnover < ₹2 Cr)",
        features: [
          "AOC-4 filing",
          "MGT-7A filing",
          "DIR-3 KYC (2 directors)",
          "AGM documentation",
          "Compliance calendar"
        ]
      },
      {
        name: "Growth",
        price: "₹14,999",
        description: "For growing companies",
        features: [
          "All filings in Startup",
          "Audit coordination",
          "All director KYC",
          "Event-based filings",
          "Quarterly review"
        ],
        popular: true
      },
      {
        name: "Enterprise",
        price: "₹29,999",
        description: "Complete compliance management",
        features: [
          "All filings",
          "Board meeting support",
          "Statutory registers",
          "Compliance manager",
          "Unlimited support"
        ]
      }
    ],
    governmentFees: "Normal filing fees: ₹200-600 per form. Late fees can be up to 12x of normal fees."
  },
  
  faqs: [
    { question: "What are the due dates for annual filing?", answer: "AGM: Within 6 months of FY end (Sept 30). AOC-4: Within 30 days of AGM. MGT-7: Within 60 days of AGM. Director KYC: September 30 each year." },
    { question: "What if company has no business activity?", answer: "Even dormant companies with nil transactions must file annual returns. Failure leads to penalties and eventual striking off." },
    { question: "What are penalties for late filing?", answer: "Additional fees range from ₹100 to ₹600 per form per day. Extended delay leads to prosecution of directors and company." },
    { question: "Can directors be disqualified?", answer: "Yes, directors of companies not filing returns for 3 continuous years can be disqualified for 5 years under Section 164(2)." },
    { question: "Is audit mandatory for all companies?", answer: "Yes, all companies must get accounts audited. Small companies have some relaxations but audit is still required." },
    { question: "What about LLP annual filing?", answer: "LLPs must file Form 8 (Statement of Account) by October 30 and Form 11 (Annual Return) by May 30 each year." }
  ],
  
  relatedServices: [
    { id: "roc-filing", label: "ROC Filing" },
    { id: "director-kyc", label: "Director KYC" },
    { id: "gst-return-filing", label: "GST Returns" },
    { id: "income-tax-filing", label: "ITR Filing" },
    { id: "audit-services", label: "Audit Services" },
    { id: "virtual-cfo", label: "Virtual CFO" }
  ],
  
  trustSignals: {
    incorporations: "8,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Expert CAs"
  }
};

// ==================== CXO SERVICES ====================

export const virtualCFO = {
  id: "virtual-cfo",
  slug: "virtual-cfo",
  seoTitle: "Virtual CFO Services India | Fractional CFO for Startups",
  seoDescription: "Get experienced CFO expertise at fraction of cost. Financial planning, fundraising support, MIS reporting, and strategic finance for startups and SMEs.",
  heroTitle: "Virtual CFO Services",
  heroSubtitle: "CXO Services",
  summary: "Virtual CFO services provide your business with experienced financial leadership without the cost of a full-time CFO. Our expert CAs and CFOs help with financial planning, cash flow management, fundraising support, investor relations, MIS reporting, and strategic decision-making for startups and growing businesses.",
  
  whoNeeds: {
    title: "Who Needs Virtual CFO?",
    description: "Businesses needing CFO-level expertise without full-time CFO costs.",
    points: [
      "Startups planning fundraising rounds",
      "SMEs seeking financial discipline",
      "Growing businesses needing MIS systems",
      "Companies preparing for audits or due diligence",
      "Businesses with complex financial structures",
      "Founders wanting to focus on core business",
      "Companies scaling operations rapidly",
      "Businesses entering new markets"
    ]
  },
  
  whyRequired: {
    title: "Why Virtual CFO Matters?",
    description: "Strategic financial leadership drives business growth and stability.",
    points: [
      "Cost Effective: Full CFO expertise at 20-30% of full-time CFO salary.",
      "Strategic Insight: Data-driven decisions with proper financial analysis.",
      "Fundraising: Expert support for investor presentations and negotiations.",
      "Compliance: Ensure all statutory and regulatory compliance.",
      "Cash Management: Optimize working capital and cash flow.",
      "Risk Management: Identify and mitigate financial risks."
    ]
  },
  
  benefits: [
    { title: "Cost Savings", description: "CFO-level expertise at fraction of full-time CFO cost." },
    { title: "Strategic Planning", description: "Long-term financial planning and business strategy." },
    { title: "Fundraising Support", description: "Investor presentations, valuations, and deal support." },
    { title: "MIS & Reporting", description: "Comprehensive dashboards and management reports." },
    { title: "Cash Flow Management", description: "Optimize working capital and improve cash position." },
    { title: "Compliance Oversight", description: "Ensure all statutory and tax compliance." },
    { title: "Flexible Engagement", description: "Scale services up or down as needed." },
    { title: "Board Support", description: "Professional representation in board meetings." }
  ],
  
  documentsRequired: {
    title: "Information for Virtual CFO Engagement",
    description: "We'll need access to understand your current financial state.",
    directors: [
      "Management team introduction",
      "Business overview presentation",
      "Key stakeholder contacts"
    ],
    company: [
      "Historical financial statements (3 years)",
      "Current year financials",
      "Bank statements",
      "Outstanding receivables/payables",
      "Existing loan documents",
      "Cap table (if applicable)",
      "Business plan and projections",
      "Current compliance status"
    ]
  },
  
  process: [
    { step: 1, title: "Discovery Call", description: "Understand your business, challenges, and CFO requirements.", duration: "1 hour" },
    { step: 2, title: "Financial Assessment", description: "Review current financial state and identify gaps.", duration: "1 week" },
    { step: 3, title: "Engagement Setup", description: "Define scope, deliverables, and communication cadence.", duration: "1 day" },
    { step: 4, title: "System Implementation", description: "Set up reporting systems, MIS dashboards, and processes.", duration: "2-4 weeks" },
    { step: 5, title: "Ongoing Support", description: "Regular reviews, reporting, and strategic guidance.", duration: "Monthly" },
    { step: 6, title: "Quarterly Review", description: "Comprehensive quarterly business and financial review.", duration: "Quarterly" }
  ],
  
  timeline: {
    total: "Ongoing engagement",
    breakdown: [
      { phase: "Initial assessment", duration: "1-2 weeks" },
      { phase: "System setup", duration: "2-4 weeks" },
      { phase: "Steady state", duration: "Ongoing" }
    ],
    note: "Engagement typically starts with 6-month commitment with monthly billing."
  },
  
  pricing: {
    title: "Virtual CFO Pricing",
    description: "Flexible plans based on your business needs.",
    packages: [
      {
        name: "Essential",
        price: "₹25,000/month",
        description: "For early-stage startups",
        features: [
          "Monthly MIS reports",
          "Cash flow monitoring",
          "Basic compliance oversight",
          "Quarterly review calls",
          "Email support"
        ]
      },
      {
        name: "Growth",
        price: "₹50,000/month",
        description: "For growing businesses",
        features: [
          "Weekly MIS dashboards",
          "Fundraising support",
          "Board deck preparation",
          "Tax planning",
          "Dedicated CFO calls"
        ],
        popular: true
      },
      {
        name: "Enterprise",
        price: "₹1,00,000/month",
        description: "Full CFO coverage",
        features: [
          "Daily financial monitoring",
          "Investor relations",
          "M&A support",
          "Team training",
          "On-site presence"
        ]
      }
    ],
    governmentFees: "No government fees. Professional service engagement."
  },
  
  faqs: [
    { question: "How is Virtual CFO different from accountant?", answer: "Accountants handle bookkeeping and compliance. Virtual CFO provides strategic financial leadership, planning, fundraising support, and business advisory - looking forward, not just backward." },
    { question: "How many hours per month?", answer: "Varies by plan - typically 10-40 hours monthly. Includes regular calls, report preparation, and ad-hoc support as needed." },
    { question: "Can Virtual CFO help with fundraising?", answer: "Yes, fundraising support is a core service. Includes financial model preparation, investor deck review, due diligence support, and negotiation assistance." },
    { question: "What tools do you use?", answer: "We work with your existing tools or recommend appropriate solutions - Tally, Zoho Books, QuickBooks, Excel, and custom MIS dashboards." },
    { question: "Is it suitable for pre-revenue startups?", answer: "Yes, we have plans specifically designed for early-stage startups focusing on runway management, fundraising preparation, and basic financial discipline." },
    { question: "How quickly can you start?", answer: "Engagement can begin within 1 week of agreement. Full MIS setup typically takes 2-4 weeks depending on data availability." }
  ],
  
  relatedServices: [
    { id: "payroll-compliance", label: "Payroll Services" },
    { id: "annual-filing", label: "Compliance Filing" },
    { id: "income-tax-filing", label: "Tax Filing" },
    { id: "startup-india", label: "Startup India" },
    { id: "private-limited-company", label: "Company Registration" },
    { id: "gst-return-filing", label: "GST Returns" }
  ],
  
  trustSignals: {
    incorporations: "200+",
    rating: "4.9/5",
    experience: "15+ Years",
    support: "Expert CFOs"
  }
};

// Export all services
export const ipServices = {
  trademarkSearch,
  trademarkObjection,
  copyrightRegistration
};

export const licenseServices = {
  fssaiLicense,
  iecCode
};

export const complianceServices = {
  annualFiling
};

export const cxoServices = {
  virtualCFO
};
